import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { Form, Input, Button, Upload, Select, Card, Typography, Divider, Row, Col, Checkbox } from "antd";
import { RocketOutlined, UserOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, LockOutlined, PlusOutlined, EnvironmentOutlined, FileTextOutlined, BankOutlined, TeamOutlined, CalendarOutlined, DollarOutlined } from "@ant-design/icons";
// import MapPicker from '../MapPicker';
import { fetchIndustries } from '../../api/industryApi';

const StartupRegister = ({ editingStartup = null, isAdminEdit = false, selfEdit = false, onSuccess = null }) => {
  const [formData, setFormData] = useState({
    startupName: '',
    founderName: '',
    description: '',
    email: '',
    phone: '',
    industry: '',
    website: '',
    stage: 'Idea',
    foundedYear: new Date().getFullYear(),
    teamSize: '1-5',
    fundingStage: 'Bootstrapped',
    password: '',
    password2: '',
    logo: null,
    banner: null,
    pitchDeck: null,
    latitude: 20.9374, // Default to Vidarbha region
    longitude: 77.7796,
  });

  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [pitchDeckName, setPitchDeckName] = useState('');
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);
  const navigate = useNavigate();

  const {
    startupName,
    founderName,
    description,
    email,
    phone,
    industry,
    website,
    stage,
    foundedYear,
    teamSize,
    fundingStage,
    password,
    password2,
  } = formData;

  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const response = await fetchIndustries();
        console.log('Industries API response:', response);
        const industriesData = response?.data || response?.industries || response;
        if (Array.isArray(industriesData)) {
          setIndustries(industriesData);
        } else {
          console.error('Invalid industries data format:', response);
          setIndustries([]);
        }
        setLoadingIndustries(false);
      } catch (error) {
        console.error('Error fetching industries:', error);
        toast.error('Failed to load industries');
        setLoadingIndustries(false);
      }
    };
    loadIndustries();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingStartup && isAdminEdit) {
      setFormData({
        startupName: editingStartup.startupName || '',
        founderName: editingStartup.founderName || '',
        description: editingStartup.description || '',
        email: editingStartup.email || '',
        phone: editingStartup.phone || '',
        industry: editingStartup.industry || '',
        website: editingStartup.website || '',
        stage: editingStartup.stage || 'Idea',
        foundedYear: editingStartup.foundedYear || new Date().getFullYear(),
        teamSize: editingStartup.teamSize || '1-5',
        fundingStage: editingStartup.fundingStage || 'Bootstrapped',
        password: '',
        password2: '',
        logo: null,
        banner: null,
        pitchDeck: null,
        latitude: editingStartup.latitude || 20.9374,
        longitude: editingStartup.longitude || 77.7796,
      });

      // Set logo preview
      if (editingStartup.logo) {
        let logoUrl = editingStartup.logo;
        if (!logoUrl.startsWith('http')) {
          if (logoUrl.startsWith('/')) {
            logoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${logoUrl}`;
          } else {
            logoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${logoUrl}`;
          }
        }
        setLogoPreview(logoUrl);
      }

      // Set banner preview
      if (editingStartup.banner) {
        let bannerUrl = editingStartup.banner;
        if (!bannerUrl.startsWith('http')) {
          if (bannerUrl.startsWith('/')) {
            bannerUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${bannerUrl}`;
          } else {
            bannerUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${bannerUrl}`;
          }
        }
        setBannerPreview(bannerUrl);
      }

      // Set pitch deck name
      if (editingStartup.pitchDeck) {
        const fileName = editingStartup.pitchDeck.split('/').pop();
        setPitchDeckName(fileName);
      }
    }
  }, [editingStartup, isAdminEdit]);

  const onChange = (e) => {
    if (e.target) {
      // Handle regular inputs and file inputs  
      if (e.target.name === 'logo' || e.target.name === 'banner') {
        const file = e.target.files ? e.target.files[0] : null;
        setFormData({ ...formData, [e.target.name]: file });

        // Create preview
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (e.target.name === 'logo') {
              setLogoPreview(reader.result);
            } else {
              setBannerPreview(reader.result);
            }
          };
          reader.readAsDataURL(file);
        }
      } else if (e.target.name === 'pitchDeck') {
        const file = e.target.files ? e.target.files[0] : null;
        setFormData({ ...formData, [e.target.name]: file });
        setPitchDeckName(file ? file.name : '');
      } else {
        setFormData({ ...formData, [e.target.name]: e.target.value });
      }
    }
  };

  const handleLocationSelect = ({ latitude, longitude }) => {
    setFormData(prev => ({
      ...prev,
      latitude: parseFloat(latitude.toFixed(6)),
      longitude: parseFloat(longitude.toFixed(6))
    }));
    //console.log('Location updated:', { latitude, longitude });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match (only for new registrations)
    if (!isAdminEdit && password !== password2) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate required fields
    if (!startupName || !founderName || !description || !email || !phone || !industry) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate logo is uploaded (only for new registrations)
    if (!isAdminEdit && !formData.logo) {
      toast.error('Please upload a startup logo');
      return;
    }

    // Validate location
    if (!formData.latitude || !formData.longitude) {
      toast.error('Please select your startup location on the map');
      return;
    }

    setLoading(true);
    
    const formDataToSend = new FormData();
    formDataToSend.append('startupName', startupName);
    formDataToSend.append('founderName', founderName);
    formDataToSend.append('description', description);
    formDataToSend.append('email', email);
    formDataToSend.append('phone', phone);
    formDataToSend.append('industry', industry);
    formDataToSend.append('website', website || '');
    formDataToSend.append('stage', stage);
    formDataToSend.append('foundedYear', foundedYear);
    formDataToSend.append('teamSize', teamSize);
    formDataToSend.append('fundingStage', fundingStage);
    
    // Don't send passwords when editing
    if (!isAdminEdit) {
      formDataToSend.append('password', password);
      formDataToSend.append('password2', password2);
    }
    
    formDataToSend.append('latitude', formData.latitude.toString());
    formDataToSend.append('longitude', formData.longitude.toString());
    
    // Only append files if they were changed
    if (formData.logo) {
      formDataToSend.append('logo', formData.logo);
    }
    
    if (formData.banner) {
      formDataToSend.append('banner', formData.banner);
    }
    
    if (formData.pitchDeck) {
      formDataToSend.append('pitchDeck', formData.pitchDeck);
    }

    try {
      let response;
      
      if (isAdminEdit && editingStartup) {
        // Update existing startup — self-editing users hit the self-service
        // route (auth-only), admins hit the admin-only management route.
        if (selfEdit) {
          response = await axios.put(`/api/startup/${editingStartup._id}`, formDataToSend, {
            headers: { 'x-auth-token': localStorage.getItem('token') }
          });
        } else {
          response = await api.put(`/admin/startups/${editingStartup._id}`, formDataToSend);
        }
        toast.success('Startup updated successfully');
        if (onSuccess) onSuccess();
      } else {
        // Register new startup
        // Don't set Content-Type manually — axios sets it automatically for
        // FormData with the correct multipart boundary.
        const config = {
          withCredentials: true,
          timeout: 30000, // 30 second timeout
        };

        response = await axios.post('/api/auth/register/startup', formDataToSend, config);
        
        if (response.data.token) {
          // Save token to localStorage
          localStorage.setItem('token', response.data.token);

          // Save user data (dashboard/profile pages read this under 'userInfo')
          localStorage.setItem('userInfo', JSON.stringify(response.data.user));
        }
        
        toast.success('Startup registration successful! Welcome to our platform.');
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Registration/Update error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      
      if (err.response) {
        if (err.response.data && err.response.data.errors) {
          err.response.data.errors.forEach((error) => {
            toast.error(error.msg || 'An error occurred');
          });
        } else if (err.response.data && err.response.data.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error(`Server error: ${err.response.status} ${err.response.statusText}`);
        }
      } else if (err.request) {
        toast.error('No response from server. Please try again later.');
      } else {
        toast.error('Error setting up request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate year options for founded year (last 20 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear; year >= currentYear - 20; year--) {
    yearOptions.push(year);
  }

  // Team size options
  const teamSizeOptions = [
    '1-5',
    '6-10',
    '11-20',
    '21-50',
    '51-100',
    '100+'
  ];

  // Funding stage options
  const fundingStages = [
    'Pre-seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C+',
    'Bootstrapped'
  ];


// Startup stage options
const startupStages = [
  'Idea',
  'Prototype',
  'MVP',
  'Early Revenue',
  'Scaling',
  'Mature'
];

  // Ant Design styling
  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };

  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

return (
  <div style={{ padding: isAdminEdit ? "0" : "20px", maxWidth: "900px", margin: "0 auto" }}>
    {!isAdminEdit && (
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <Typography.Title level={2} style={{ ...baseStyle, fontSize: "28px", fontWeight: 500, color: "#470172" }}>
          Startup Registration
        </Typography.Title>
        <Typography.Text style={{ ...baseStyle, fontSize: "16px", opacity: 0.7 }}>
          Join our platform and get access to investors, mentors, and more
        </Typography.Text>
      </div>
    )}

    <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(71, 1, 114, 0.10)", marginBottom: 32 }}>
      <form onSubmit={onSubmit}>
      <Form layout="vertical" component={false} requiredMark={false}>
        {/* Logo Upload */}
        <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
          Startup Logo <span style={{ color: 'red' }}>*</span>
        </Typography.Title>
        <Form.Item style={{ marginBottom: 20 }}>
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            accept="image/*"
            maxCount={1}
            onChange={(info) => {
              const file = info.fileList[0]?.originFileObj;
              if (file) {
                onChange({ target: { name: 'logo', files: [file], type: 'file' } });
              }
            }}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="logo" style={{ width: '100%', height: 120, objectFit: 'contain', borderRadius: 8 }} />
            ) : (
              uploadButton
            )}
          </Upload>
          <Typography.Text style={{ fontSize: 12, color: '#999' }}>
            Upload your startup logo (Max 5MB)
          </Typography.Text>
        </Form.Item>

        <Divider style={{ margin: "24px 0" }} />

        {/* Startup Details */}
        <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
          Startup Details
        </Typography.Title>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={<span style={labelStyle}>Startup Name <span style={{ color: 'red' }}>*</span></span>}>
              <Input 
                prefix={<RocketOutlined />} 
                name="startupName"
                value={startupName}
                onChange={onChange}
                placeholder="Enter your startup name"
                required
                style={{ borderRadius: 6 }}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Official name of your startup
              </Typography.Text>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Founder Name <span style={{ color: 'red' }}>*</span></span>}>
              <Input 
                prefix={<UserOutlined />} 
                name="founderName"
                value={founderName}
                onChange={onChange}
                placeholder="Full name"
                required
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Email <span style={{ color: 'red' }}>*</span></span>}>
              <Input 
                prefix={<MailOutlined />} 
                name="email"
                type="email"
                value={email}
                onChange={onChange}
                placeholder="you@example.com"
                required
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Phone <span style={{ color: 'red' }}>*</span></span>}>
              <Input 
                prefix={<PhoneOutlined />} 
                name="phone"
                value={phone}
                onChange={onChange}
                placeholder="+91 1234567890"
                required
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Industry <span style={{ color: 'red' }}>*</span></span>}>
              <Select
                value={industry}
                onChange={(value) => setFormData({ ...formData, industry: value })}
                placeholder="Select industry"
                style={{ width: '100%', borderRadius: 6 }}
                loading={loadingIndustries}
              >
                {industries && industries.length > 0 ? (
                  industries.map((ind) => {
                    const industryId = ind?._id || ind;
                    const industryName = ind?.sectorName || ind?.name || ind;
                    return (
                      <Select.Option key={industryId} value={industryId}>
                        {industryName}
                      </Select.Option>
                    );
                  })
                ) : (
                  <Select.Option value="">No industries available</Select.Option>
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Website</span>}>
              <Input 
                prefix={<GlobalOutlined />} 
                name="website"
                type="url"
                value={website}
                onChange={onChange}
                placeholder="https://yourstartup.com"
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Current Stage <span style={{ color: 'red' }}>*</span></span>}>
              <Select
                value={stage}
                onChange={(value) => setFormData({ ...formData, stage: value })}
                style={{ width: '100%', borderRadius: 6 }}
              >
                {startupStages.map(s => (
                  <Select.Option key={s} value={s}>{s}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Team Size</span>}>
              <Select
                value={teamSize}
                onChange={(value) => setFormData({ ...formData, teamSize: value })}
                style={{ width: '100%', borderRadius: 6 }}
                suffixIcon={<TeamOutlined />}
              >
                {teamSizeOptions.map(size => (
                  <Select.Option key={size} value={size}>{size}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Funding Stage</span>}>
              <Select
                value={fundingStage}
                onChange={(value) => setFormData({ ...formData, fundingStage: value })}
                style={{ width: '100%', borderRadius: 6 }}
                suffixIcon={<DollarOutlined />}
              >
                {fundingStages.map(fs => (
                  <Select.Option key={fs} value={fs}>{fs}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Founded Year</span>}>
              <Select
                value={foundedYear}
                onChange={(value) => setFormData({ ...formData, foundedYear: value })}
                style={{ width: '100%', borderRadius: 6 }}
                suffixIcon={<CalendarOutlined />}
              >
                {yearOptions.map(year => (
                  <Select.Option key={year} value={year}>{year}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label={<span style={labelStyle}>Startup Description <span style={{ color: 'red' }}>*</span></span>}>
              <Input.TextArea
                name="description"
                value={description}
                onChange={onChange}
                rows={5}
                placeholder="Tell us about your startup, what problem it solves, and what makes it unique..."
                required
                style={{ borderRadius: 6 }}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Minimum 100 characters
              </Typography.Text>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "24px 0" }} />

        {/* Additional Uploads */}
        <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
          Additional Documents
        </Typography.Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Banner Image (Optional)</span>}>
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                accept="image/*"
                maxCount={1}
                onChange={(info) => {
                  const file = info.fileList[0]?.originFileObj;
                  if (file) {
                    onChange({ target: { name: 'banner', files: [file], type: 'file' } });
                  }
                }}
              >
                {bannerPreview ? (
                  <img src={bannerPreview} alt="banner" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }} />
                ) : (
                  uploadButton
                )}
              </Upload>
              <Typography.Text style={{ fontSize: 12, color: '#999' }}>
                Upload banner image (Max 5MB)
              </Typography.Text>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={<span style={labelStyle}>Pitch Deck (Optional)</span>}>
              <Upload
                maxCount={1}
                beforeUpload={() => false}
                accept=".pdf"
                onChange={(info) => {
                  const file = info.fileList[0]?.originFileObj;
                  if (file) {
                    onChange({ target: { name: 'pitchDeck', files: [file], type: 'file' } });
                  }
                }}
              >
                <Button icon={<FileTextOutlined />}>
                  {pitchDeckName || 'Upload Pitch Deck'}
                </Button>
              </Upload>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                PDF up to 10MB
              </Typography.Text>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "24px 0" }} />

        {/* Account Security */}
        {!isAdminEdit && (
          <>
            <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
              Account Security
            </Typography.Title>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={<span style={labelStyle}>Password <span style={{ color: 'red' }}>*</span></span>}>
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    name="password"
                    value={password}
                    onChange={onChange}
                    placeholder="Create password"
                    required
                    minLength={6}
                    style={{ borderRadius: 6 }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={<span style={labelStyle}>Confirm Password <span style={{ color: 'red' }}>*</span></span>}>
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    name="password2"
                    value={password2}
                    onChange={onChange}
                    placeholder="Re-enter password"
                    required
                    minLength={6}
                    style={{ borderRadius: 6 }}
                  />
                  {password2 && (
                    <Typography.Text style={{ 
                      fontSize: 12, 
                      color: password === password2 ? '#52c41a' : '#ff4d4f',
                      display: 'block',
                      marginTop: 8
                    }}>
                      {password === password2 ? '✓ Passwords match' : '✗ Passwords do not match'}
                    </Typography.Text>
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "24px 0" }} />

            <Form.Item style={{ marginBottom: 20 }}>
              <Checkbox required>
                I agree to the <a href="/terms" style={{ color: '#470172' }}>Terms of Service</a> and <a href="/privacy" style={{ color: '#470172' }}>Privacy Policy</a>
              </Checkbox>
            </Form.Item>
          </>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            block
            size="large"
            style={{ 
              background: 'linear-gradient(to right, #672f89, #8e44ad)', 
              borderColor: '#672f89',
              borderRadius: 8,
              height: 48,
              fontSize: 16,
              fontWeight: 500
            }}
          >
            {loading ? 'Processing...' : 'Register Startup'}
          </Button>
        </Form.Item>

        {!isAdminEdit && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Typography.Text>
              Already have an account? <a href="/login" style={{ color: '#470172' }}>Sign in here</a>
            </Typography.Text>
          </div>
        )}
      </Form>
      </form>
    </Card>
  </div>
);

}

export default StartupRegister;
